import { SubscriptionOptions } from '@reduxjs/toolkit/dist/query/core/apiState';
import { uniqBy } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import {
  CursorPaginatedResource,
  CursorPaginationQueryParams,
  UseCursorPaginationLazyQuery,
} from './types';

interface ICursorPaginator<Item> {
  list: Item[] | undefined;
  isLoading: boolean;
  hasNext: boolean;
  hasPrevious: boolean;
  init: () => void;
  patchAllItems: (projectionFn: (r: Item) => Item) => void;
  loadMore: (direction: 'previous' | 'next', limit?: any) => void;
  loadPrevious: (limit?: number) => void;
  loadNext: (limit?: number) => void;
}

function mergeUniq<T>(l1: T[], l2: T[], iteratee = 'id'): T[] {
  return uniqBy(l1.concat(l2), iteratee);
}

export function useCursorPagination<Q extends UseCursorPaginationLazyQuery>(
  lazyQuery: Q,
  params: CursorPaginationQueryParams<Q>,
  options: { skip?: boolean } = {
    skip: false,
  },
  subscriptionOptions?: SubscriptionOptions,
): ICursorPaginator<CursorPaginatedResource<Q>> {
  const [head, setHead] = useState<string | null>(null);
  const [tail, setTail] = useState<string | null>(null);
  const [list, setList] = useState<CursorPaginatedResource<Q>[]>([]);
  const [trigger] = lazyQuery(subscriptionOptions);

  // not using the rtk query isLoading
  // because it caches the lazy response of past executions
  const [isLoading, setIsLoading] = useState(true);

  const init = useCallback(() => {
    // !! BUG (RTK Query) => refetching same request when no parameters was changed
    //  returns a cached response of last request although it is correct in the redux store
    trigger({
      ...params,
      start_from: params.start_from,
      direction: params.direction,
      limit: params.limit,
    })
      .unwrap()
      .then(({ data, pagination }) => {
        setIsLoading(false);
        setList((_) => data);
        setHead((_) => pagination.start_cursor);
        setTail((_) =>
          pagination.has_next_page ? pagination.end_cursor : null,
        );
      });
  }, []);

  useEffect(init, []);

  const loadPrevious = useCallback(
    (limit: number = 10) => {
      if (!head) return;

      trigger({
        filters: params.filters,
        start_from: head,
        direction: 'previous',
        limit,
      })
        .unwrap()
        .then(({ data, pagination }) => {
          let newItems = data.slice();
          newItems.pop();
          setList((list) => mergeUniq(data, list));

          setHead((_) => pagination.start_cursor);
        });
    },
    [head, JSON.stringify(params)],
  );

  const loadNext = useCallback(
    (limit: number = 10) => {
      if (!tail) return;

      trigger({
        filters: params.filters,
        start_from: tail,
        direction: 'next',
        limit,
      })
        .unwrap()
        .then(({ data, pagination }) => {
          setList((list) => mergeUniq(list, data));
          setTail((_) =>
            pagination.has_next_page ? pagination.end_cursor : null,
          );
        });
    },
    [tail, JSON.stringify(params)],
  );

  const loadMore = useCallback(
    (direction: 'previous' | 'next', limit = 10) => {
      return direction === 'previous' ? loadPrevious(limit) : loadNext(limit);
    },
    [loadPrevious, loadNext],
  );

  const patchAllItems = useCallback(
    (
      projectionFn: (
        r: CursorPaginatedResource<Q>,
      ) => CursorPaginatedResource<Q>,
    ) => {
      setList((l) => l.map(projectionFn));
    },
    [list],
  );

  const hasPrevious = !!head;
  const hasNext = !!tail;

  return {
    list: isLoading ? undefined : list,
    init,
    patchAllItems,
    loadMore,
    loadPrevious,
    loadNext,
    isLoading,
    hasNext,
    hasPrevious,
  };
}
